export type MemberFilter = "all" | "member" | "public";

export type AdminRole = "admin" | "viewer";
export type AdminStatus = "pending" | "active" | "disabled";

export type AdminUser = {
  googleSubject: string;
  email: string;
  displayName: string;
  pictureUrl: string;
  role: AdminRole;
  status: AdminStatus;
  createdAt: string;
  lastLoginAt: string;
  activatedAt: string | null;
  activatedBy: string | null;
  protected?: boolean;
};

export type AuthConfig = {
  googleClientId: string;
  configured: boolean;
};

export type AuthResponse =
  | { authenticated: true; user: AdminUser }
  | { authenticated: false; user: null };

export type QueryParams = {
  from: string;
  to: string;
  member: MemberFilter;
  q?: string;
  author?: string;
  limit?: number;
};

export type OverviewResponse = {
  range: { from: string; to: string; member: MemberFilter };
  totals: {
    views: number;
    unique_visitors: number;
    member_views: number;
    public_views: number;
    avg_duration_ms: number;
  };
  daily: Array<{
    view_date: string;
    views: number;
    unique_visitors: number;
    member_views: number;
    public_views: number;
  }>;
};

export type ArticleRow = {
  article_id: string;
  article_url?: string;
  canonical_url: string;
  published_date: string;
  title: string;
  author: string;
  categories: string[];
  is_member_only: number;
  views: number;
  unique_visitors: number;
  avg_duration_ms: number;
};

export type AuthorRow = {
  author: string;
  views: number;
  member_views: number;
  public_views: number;
  article_count: number;
};

export type DimensionRow = {
  name: string;
  views: number;
  percentage: number;
};

export type HongKongDistrictResponse = {
  items: DimensionRow[];
  coverage: {
    hk_views: number;
    mapped_views: number;
    unmapped_views: number;
    percentage: number;
  };
};

export type TimeSlotDimension = "device" | "geo";
export type TimeSlotSegmentLimit = number | "all";

export type DeviceHourlySlot = {
  hour: number;
  device_type: string;
  views: number;
  percentage: number;
};

export type DeviceWeeklySlot = {
  weekday: number;
  weekday_label: string;
  hour: number;
  views: number;
  percentage: number;
};

export type DeviceTimeSlotsResponse = {
  range: { from: string; to: string; member: MemberFilter };
  hourly: { results: DeviceHourlySlot[] };
  weekly: { results: DeviceWeeklySlot[] };
};

export type TimeSlotSegmentRow = {
  segment: string;
  views: number;
  percentage: number;
};

export type TimeSlotHourlyRow = {
  hour: number;
  segment: string;
  views: number;
  percentage: number;
};

export type TimeSlotWeeklyRow = {
  weekday: number;
  weekday_label: string;
  hour: number;
  segment: string;
  views: number;
  percentage: number;
};

export type TimeSlotAnalysisResponse = {
  range: { from: string; to: string; member: MemberFilter };
  dimension: TimeSlotDimension;
  segments: { results: TimeSlotSegmentRow[] };
  hourly: { results: TimeSlotHourlyRow[] };
  weekly: { results: TimeSlotWeeklyRow[] };
};

export type DevSeedResponse = {
  articles: number;
  events: number;
  attemptedEvents: number;
  from: string;
  to: string;
};

export type PublicRankingPeriod = "today" | "week" | "month";
export type SimplePublicRankingPeriod = "day" | "week" | "month";

export type PublicRankingItem = {
  rank: number;
  articleId: string;
  articleUrl: string;
  title: string;
  author: string;
  canonicalUrl: string;
  publishedDate: string;
  isMemberOnly: boolean;
  views: number;
  uniqueVisitors: number;
};

export type PublicRankingResponse = {
  period: PublicRankingPeriod;
  timeZone: "Asia/Taipei";
  from: string;
  to: string;
  generatedAt: string;
  items: PublicRankingItem[];
};

export type SimplePublicRankingItem = {
  link: string;
  title: string;
};

function buildQuery(params: QueryParams): string {
  const search = new URLSearchParams();
  search.set("from", params.from);
  search.set("to", params.to);
  search.set("member", params.member);
  if (params.q) search.set("q", params.q);
  if (params.author) search.set("author", params.author);
  if (params.limit) search.set("limit", String(params.limit));
  return search.toString();
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`API ${response.status}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method: "POST", credentials: "include" };
  if (body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const response = await fetch(path, init);
  if (!response.ok) {
    throw new Error(`API ${response.status}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const response = await fetch(path, { method: "DELETE", credentials: "include" });
  if (!response.ok) {
    throw new Error(`API ${response.status}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`API ${response.status}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  authConfig: () => apiGet<AuthConfig>("/admin/api/auth/config"),
  me: () => apiGet<AuthResponse>("/admin/api/me"),
  googleLogin: (credential: string) => apiPost<Extract<AuthResponse, { authenticated: true }>>("/admin/api/auth/google", { credential }),
  logout: () => apiPost<{ ok: true }>("/admin/api/auth/logout"),
  users: () => apiGet<{ items: AdminUser[] }>("/admin/api/users"),
  updateUser: (googleSubject: string, changes: { status: AdminStatus; role: AdminRole }) =>
    apiPatch<{ user: AdminUser }>(`/admin/api/users/${encodeURIComponent(googleSubject)}`, changes),
  overview: (params: QueryParams) => apiGet<OverviewResponse>(`/admin/api/overview?${buildQuery(params)}`),
  articles: (params: QueryParams) => apiGet<{ items: ArticleRow[] }>(`/admin/api/articles?${buildQuery(params)}`),
  authors: (params: QueryParams) => apiGet<{ items: AuthorRow[] }>(`/admin/api/authors?${buildQuery(params)}`),
  referrers: (params: QueryParams) => apiGet<{ items: DimensionRow[] }>(`/admin/api/referrers?${buildQuery(params)}`),
  geo: (params: QueryParams) => apiGet<{ items: DimensionRow[] }>(`/admin/api/geo?${buildQuery(params)}`),
  hongKongDistricts: (params: QueryParams) => apiGet<HongKongDistrictResponse>(`/admin/api/geo/hk-districts?${buildQuery(params)}`),
  devices: (params: QueryParams) => apiGet<{ items: DimensionRow[] }>(`/admin/api/devices?${buildQuery(params)}`),
  deviceTimeSlots: (params: QueryParams) => apiGet<DeviceTimeSlotsResponse>(`/admin/api/devices/time-slots?${buildQuery(params)}`),
  timeSlots: (params: QueryParams, dimension: TimeSlotDimension, segmentLimit?: TimeSlotSegmentLimit) => {
    const extra = segmentLimit === undefined ? "" : `&segmentLimit=${encodeURIComponent(String(segmentLimit))}`;
    return apiGet<TimeSlotAnalysisResponse>(`/admin/api/time-slots?${buildQuery(params)}&dimension=${encodeURIComponent(dimension)}${extra}`);
  },
  article: (articleId: string, params: QueryParams) =>
    apiGet<{ article: ArticleRow | null; daily: OverviewResponse["daily"]; hourly: Array<{ view_hour: string; views: number; unique_visitors: number }> }>(
      `/admin/api/articles/${articleId}?${buildQuery(params)}`
    ),
  exportUrl: (params: QueryParams) => `/admin/api/export.csv?${buildQuery(params)}`,
  seedLocalDevData: () => apiPost<DevSeedResponse>("/admin/api/dev/seed"),
  publicRanking: (period: PublicRankingPeriod, params: { member: MemberFilter; limit: number }) =>
    apiGet<PublicRankingResponse>(
      `/_view-counter/rankings/${period}?member=${encodeURIComponent(params.member)}&limit=${encodeURIComponent(
        String(params.limit)
      )}`
    ),
  simplePublicRanking: (period: SimplePublicRankingPeriod) => apiGet<SimplePublicRankingItem[]>(`/_view-counter/rank_of_${period}`)
};
